import { Copy } from "lucide-react"
import { SearchableSelect } from "@/components/ui/SearchableSelect"
import { SectionCard } from "@/components/ui/SectionCard"
import companyFormStyles from "@/features/company/styles/companyAdd.module.css"
import { transferSearchableTriggerClass } from "../../../constants/transferProcessStyles"
import { usesTransferWizardCardLayout } from "../../../utils/transferPropertyCompanyWizard"
import type { TransferPropertyInfoSectionProps } from "./propertyInfoSectionTypes"

export function TransferPropertySplitHeaderRow({ ctx }: TransferPropertyInfoSectionProps) {
  const {
    fragment,
    companyList,
    companyListOld,
    fkCompanyId,
    register,
    watch,
    setValueValidated,
    loadCompany,
    debouncedLoadCompanySearch,
    loadCompanyOld,
    loadProperty,
    companyOptionsLoading,
    companyOldOptionsLoading,
  } = ctx

  const useCardLayout = usesTransferWizardCardLayout(fragment)
  const splitHeaderFieldGridClass = useCardLayout
    ? "grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4"
    : companyFormStyles.formRow
  const showOldCompanyRequired = !String(watch("old_company_id") ?? "").trim()
  const showNewCompanyRequired = !String(fkCompanyId ?? "").trim()

  const fields = (
    <div
      data-testid="transfer-process-property-form-split-row"
      className={splitHeaderFieldGridClass}
    >
      <div className={useCardLayout ? undefined : companyFormStyles.formCol}>
        <div className={companyFormStyles.formGroup}>
          <label
            data-testid="transfer-process-property-form-split-old-company-label"
            className={companyFormStyles.formLabel}
          >
            Old company<span className={companyFormStyles.requiredStar}>*</span>
          </label>
          <SearchableSelect
            className={transferSearchableTriggerClass}
            placeholder="Select Company"
            options={companyListOld}
            value={watch("old_company_id")}
            triggerDataTestId="transfer-process-property-form-split-old-company-select"
            onChange={(v) => {
              const id = Array.isArray(v) ? v[0] : v
              setValueValidated("old_company_id", id)
            }}
            onOpen={() => void loadCompanyOld(undefined, { silent: true })}
            onSearch={(s) => void loadCompanyOld(s)}
            invokeSearchOnClose={false}
            loading={companyOldOptionsLoading}
          />
          {showOldCompanyRequired ? (
            <p
              data-testid="transfer-process-property-form-split-old-company-error"
              className="mt-1.5 text-xs text-rose-600"
            >
              Company Selection is required.
            </p>
          ) : null}
        </div>
      </div>
      <div className={useCardLayout ? undefined : companyFormStyles.formCol}>
        <div className={companyFormStyles.formGroup}>
          <label
            data-testid="transfer-process-property-form-split-old-property-name-label"
            className={companyFormStyles.formLabel}
          >
            Old Property Name
          </label>
          <input
            type="text"
            data-testid="transfer-process-property-form-split-old-property-name-input"
            className={companyFormStyles.customFormControl}
            {...register("property_name")}
          />
        </div>
      </div>
      <div className={useCardLayout ? undefined : companyFormStyles.formCol}>
        <div className={companyFormStyles.formGroup}>
          <label
            data-testid="transfer-process-property-form-split-new-company-label"
            className={companyFormStyles.formLabel}
          >
            New company<span className={companyFormStyles.requiredStar}>*</span>
          </label>
          <SearchableSelect
            className={transferSearchableTriggerClass}
            placeholder="Select Company"
            options={companyList}
            value={fkCompanyId}
            triggerDataTestId="transfer-process-property-form-split-new-company-select"
            onChange={(v) => {
              const id = Array.isArray(v) ? v[0] : v
              setValueValidated("fk_company_id", id)
              void loadProperty()
            }}
            onOpen={() => void loadCompany(undefined, { silent: true })}
            onSearch={(s) => debouncedLoadCompanySearch(s)}
            invokeSearchOnClose={false}
            loading={companyOptionsLoading}
          />
          {showNewCompanyRequired ? (
            <p
              data-testid="transfer-process-property-form-split-new-company-error"
              className="mt-1.5 text-xs text-rose-600"
            >
              Company Selection is required.
            </p>
          ) : null}
        </div>
      </div>
      <div className={useCardLayout ? undefined : companyFormStyles.formCol}>
        <div className={companyFormStyles.formGroup}>
          <label
            data-testid="transfer-process-property-form-split-new-property-name-label"
            className={companyFormStyles.formLabel}
          >
            New Property Name
          </label>
          <input
            type="text"
            data-testid="transfer-process-property-form-split-new-property-name-input"
            className={companyFormStyles.customFormControl}
            {...register("new_property_name")}
          />
        </div>
      </div>
    </div>
  )

  if (useCardLayout) {
    return (
      <SectionCard
        icon={Copy}
        title="Property Information"
        testId="transfer-process-split-property-info-section"
        titleTestId="transfer-process-split-property-info-section-title"
      >
        {fields}
      </SectionCard>
    )
  }

  return fields
}
